import React, { useEffect, useState } from "react";
import BottomNav from "../common/BottomNav";
import UserSidebar from "../common/UserSidebar";
import {
  FaPlusCircle,
  FaTools,
  FaCalendarAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import { useTranslation } from "react-i18next";
import { logout } from "../utils/auth";
import { updateServiceProviderLanguage } from "../../services/coreService";
import styles from "../css/Dashboard.module.css";

const menuItems = [
  {
    key: "add_service",
    label: "Add Service",
    icon: <FaPlusCircle size={32} color="#667eea" />,
    path: "/add-service",
  },
  {
    key: "manage",
    label: "Manage",
    icon: <FaTools size={32} color="#8b5cf6" />,
    path: "/manage",
  },
  {
    key: "update_calendar",
    label: "Update Calendar",
    icon: <FaCalendarAlt size={32} color="#3b82f6" />,
    path: "/mark-busy",
  }
];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search] = useState("");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userName = user.name || t("Dashboard.user", "User");
  const userImage =
    user.imageUrl && user.imageUrl !== "null" ? user.imageUrl : "";

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user.spId;
    if (!userId) return;
    const db = getDatabase();
    const notificationsRef = ref(db, `notifications/${userId}`);
    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const notifications = snapshot.val() || {};
      const unread = Object.values(notifications).filter(
        (n) => n.isRead === false
      ).length;
      setUnreadCount(unread);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Scroll to top when sidebar is opened or closed
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filteredMenuItems = menuItems.filter((item) =>
    t(`Dashboard.${item.key}`, item.label)
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const handleLanguageChange = async (lng) => {
    const normalizedLanguage = lng === "hi" ? "hindi" : "english";

    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);

    if (!user?.spId) {
      return;
    }

    const response = await updateServiceProviderLanguage(user.spId, normalizedLanguage);
    if (response?.responseCode === 200 && Array.isArray(response.responseData) && response.responseData[0]) {
      const updatedUser = {
        ...user,
        ...response.responseData[0],
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } else {
      console.error("Failed to update language preference:", response?.responseMessage || "Unknown error");
    }
  };

  return (
    <div className={styles.pageContainer}>
      {/* Sidebar and overlay */}
      {sidebarOpen && (
        <>
          <div
            className={styles.overlay}
            onClick={() => setSidebarOpen(false)}
          />
          <div className={styles.sidebarContainer}>
            <UserSidebar user={user} onLogout={handleLogout} onClose={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div
            className={styles.userSection}
            onClick={() => setSidebarOpen(true)}
          >
            <div className={styles.avatar}>
              {userImage ? (
                <img src={userImage} alt={userName} />
              ) : (
                getInitials(userName)
              )}
            </div>
            <span className={styles.userName}>{userName}</span>
          </div>

          <div className={styles.rightSection}>
            {/* Language Switcher */}
            <div className={styles.languageSwitcher}>
              <button
                onClick={() => handleLanguageChange("en")}
                className={`${styles.langButton} ${
                  i18n.language === "en" ? styles.langButtonActive : ""
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLanguageChange("hi")}
                className={`${styles.langButton} ${
                  i18n.language === "hi" ? styles.langButtonActive : ""
                }`}
              >
                हिं
              </button>
            </div>

            {/* Notification Icon */}
            <div
              className={styles.notificationIcon}
              onClick={() => navigate("/notifications")}
            >
              <svg width="24" height="24" fill="#fff">
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5l-1.29 1.29A1 1 0 0 0 6 19h12a1 1 0 0 0 .71-1.71L18 16z" />
              </svg>
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Wrapper - Added */}
      <div className={styles.mainContent}>
        {/* Welcome Section */}
        <div className={styles.welcomeSection}>
          <div className={styles.welcomeCard}>
            <div className={styles.welcomeDecoration1} />
            <div className={styles.welcomeDecoration2} />
            <div className={styles.welcomeContent}>
              <h1 className={styles.welcomeTitle}>
                {t("Dashboard.welcome", "Welcome")}, {userName.split(" ")[0]}! 👋
              </h1>
              <p className={styles.welcomeSubtitle}>
                {t(
                  "Dashboard.welcome_message",
                  "Manage your services, bookings, and grow your business effortlessly."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Menu Section */}
        <div className={styles.menuSection}>
          <h2 className={styles.sectionTitle}>
            {t("Dashboard.quick_actions", "Quick Actions")}
          </h2>
          <div className={styles.menuGrid}>
            {filteredMenuItems.length === 0 ? (
              <div className={styles.noResults}>
                {t("Dashboard.no_menu_items_found", "No menu items found.")}
              </div>
            ) : (
              filteredMenuItems.map((item) => (
                <div
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  className={`${styles.menuCard} ${
                    item.desktopOnly ? styles.desktopOnly : ""
                  }`}
                >
                  <div className={styles.menuIcon}>{item.icon}</div>
                  <span className={styles.menuLabel}>
                    {t(`Dashboard.${item.key}`, item.label)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
        {/* Leads Quick Card */}
        <div className={styles.leadsSection}>
          <button
            className={styles.leadsSummaryCard}
            onClick={() => navigate("/leads")}
            type="button"
          >
            <div className={styles.leadsQuickCardLeft}>
              <span className={styles.leadsQuickIcon}>🎯</span>
              <div className={styles.leadsQuickTextWrap}>
                <h3 className={styles.leadsQuickTitle}>{t("Dashboard.leads", "Leads")}</h3>
                <p className={styles.leadsQuickSubtitle}>
                  {t("Dashboard.open_leads_page", "Tap to view all customer leads")}
                </p>
              </div>
            </div>
            <span className={styles.leadsQuickArrow}>→</span>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 60,
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <BottomNav />
      </div>
    </div>
  );
}
